import type {
  LearningCounterState,
  LearningCounterStateDetails,
  UnifiedMeasurementData,
} from "bundles/projects/types";
import {
  LEARNING_COUNTER_STORAGE_KEY,
  SKIP_LEARNING_POPUP_STORAGE_KEY,
} from "bundles/projects/consts";

type LearningCountersData = Record<
  string,
  Record<number, LearningCounterState>
>;
type SkipPopupData = Record<string, boolean>;

/**
 * CRUD helpers for persisting reinforced learning counter state in localStorage
 * Storage format:
 * {
 *   project_1: { page_1: {...}, page_2: {...} },
 *   project_2: { page_1: {...}, page_2: {...} }
 * }
 */
export class ReinforcedLearningUtils {
  private static readonly STORAGE_KEY = LEARNING_COUNTER_STORAGE_KEY;
  private static readonly SKIP_POPUP_KEY = SKIP_LEARNING_POPUP_STORAGE_KEY;

  /**
   * Save learning counter state for a specific page within a project
   */
  static saveLearningCounterStorage(
    projectId: string,
    pageId: number,
    state: LearningCounterState
  ): void {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      const parsedData: LearningCountersData = existingData
        ? JSON.parse(existingData)
        : {};

      if (!parsedData[projectId]) {
        parsedData[projectId] = {};
      }

      parsedData[projectId][pageId] = state;

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error("Failed to save learning counter:", error);
    }
  }

  /**
   * Load learning counter state for a specific page within a project
   */
  static loadLearningCounterStorage(
    projectId: string,
    pageId: number
  ): LearningCounterState | null {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      if (!existingData) return null;

      const parsedData: LearningCountersData = JSON.parse(existingData);

      return parsedData[projectId]?.[pageId] || null;
    } catch (error) {
      console.error("Failed to load learning counter:", error);
      return null;
    }
  }

  /**
   * Load all learning counter states for a specific project
   */
  static loadAllLearningCountersStorage(
    projectId: string
  ): Record<number, LearningCounterState> {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      if (!existingData) return {};

      const parsedData: LearningCountersData = JSON.parse(existingData);

      return parsedData[projectId] || {};
    } catch (error) {
      console.error("Failed to load all learning counters:", error);
      return {};
    }
  }

  /**
   * Update learning counter state for a specific page within a project
   */
  static updateLearningCounterStorage(
    projectId: string,
    pageId: number,
    updates: Partial<LearningCounterState>
  ): void {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      const parsedData: LearningCountersData = existingData
        ? JSON.parse(existingData)
        : {};

      if (!parsedData[projectId]) {
        parsedData[projectId] = {};
      }

      const currentState = parsedData[projectId][pageId] || {
        counter: 0,
        shouldAnimate: false,
      };

      parsedData[projectId][pageId] = {
        ...currentState,
        ...updates,
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error("Failed to update learning counter:", error);
    }
  }

  /**
   * Delete learning counter state for a specific page within a project
   */
  static deleteLearningCounterStorage(projectId: string, pageId: number): void {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      if (!existingData) return;

      const parsedData: LearningCountersData = JSON.parse(existingData);

      if (parsedData[projectId]) {
        delete parsedData[projectId][pageId];

        // Clean up empty project entries
        if (Object.keys(parsedData[projectId]).length === 0) {
          delete parsedData[projectId];
        }
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error("Failed to delete learning counter:", error);
    }
  }

  /**
   * Clear all learning counter states for a specific project
   */
  static clearAllLearningCountersStorage(projectId: string): void {
    try {
      const existingData = localStorage.getItem(this.STORAGE_KEY);
      if (!existingData) return;

      const parsedData: LearningCountersData = JSON.parse(existingData);

      delete parsedData[projectId];

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error("Failed to clear all learning counters:", error);
    }
  }

  /**
   * Save skip first learning popup preference for a specific project
   */
  static saveSkipFirstLearningPopupStorage(
    projectId: string,
    skip: boolean
  ): void {
    try {
      const existingData = localStorage.getItem(this.SKIP_POPUP_KEY);
      const parsedData: SkipPopupData = existingData
        ? JSON.parse(existingData)
        : {};

      parsedData[projectId] = skip;

      localStorage.setItem(this.SKIP_POPUP_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error(
        "Failed to save skip first learning popup preference:",
        error
      );
    }
  }

  /**
   * Load skip first learning popup preference for a specific project
   */
  static loadSkipFirstLearningPopupStorage(projectId: string): boolean {
    try {
      const existingData = localStorage.getItem(this.SKIP_POPUP_KEY);
      if (!existingData) return false;

      const parsedData: SkipPopupData = JSON.parse(existingData);

      return parsedData[projectId] || false;
    } catch (error) {
      console.error(
        "Failed to load skip first learning popup preference:",
        error
      );
      return false;
    }
  }

  /**
   * Delete skip first learning popup preference for a specific project
   */
  static deleteSkipFirstLearningPopupStorage(projectId: string): void {
    try {
      const existingData = localStorage.getItem(this.SKIP_POPUP_KEY);
      if (!existingData) return;

      const parsedData: SkipPopupData = JSON.parse(existingData);

      delete parsedData[projectId];

      localStorage.setItem(this.SKIP_POPUP_KEY, JSON.stringify(parsedData));
    } catch (error) {
      console.error(
        "Failed to delete skip first learning popup preference:",
        error
      );
    }
  }

  /**
   * Helper to update details when renaming a scope
   * - If old scope exists: replace it with new name and update counter
   * - If old scope doesn't exist: create new entry with specified amount
   *
   * @param currentDetails - Current details array
   * @param oldScopeName - Old scope name to replace
   * @param newScopeName - New scope name
   * @param amount - Amount to change counter by (can be positive or negative, default: 1)
   * @returns Updated details array
   */
  static updateRenamedLearningScopeDetails(
    currentDetails: LearningCounterStateDetails,
    oldScopeName: string,
    newScopeName: string,
    amount: number = 1
  ): LearningCounterStateDetails {
    // Find the old scope detail (case-insensitive)
    const oldNameLower = oldScopeName.toLowerCase();
    const oldDetail = currentDetails.find(
      (detail) => detail.scopeLabel.toLowerCase() === oldNameLower
    );

    if (oldDetail) {
      // Replace old detail with new name and update counter
      return currentDetails.map((detail) =>
        detail.scopeLabel.toLowerCase() === oldNameLower
          ? {
              scopeLabel: newScopeName,
              counter: Math.max(0, detail.counter + amount),
            }
          : detail
      );
    } else {
      // Create new entry if old detail doesn't exist
      return [
        ...currentDetails,
        { scopeLabel: newScopeName, counter: Math.max(0, amount) },
      ];
    }
  }

  /**
   * Helper to update or create a scope detail
   * - If scope exists: change its counter by specified amount
   * - If scope doesn't exist: create new entry with specified amount
   *
   * @param currentDetails - Current details array
   * @param scopeName - Scope name to update or create
   * @param amount - Amount to change counter by (can be positive or negative, default: 1)
   * @returns Updated details array
   */
  static changeLearningScopeDetail(
    currentDetails: LearningCounterStateDetails,
    scopeName: string,
    amount: number = 1
  ): LearningCounterStateDetails {
    const scopeNameLower = scopeName.toLowerCase();
    const existingDetail = currentDetails.find(
      (detail) => detail.scopeLabel.toLowerCase() === scopeNameLower
    );

    if (existingDetail) {
      // Update existing scope counter, ensure it doesn't go below 0
      return currentDetails.map((detail) =>
        detail.scopeLabel.toLowerCase() === scopeNameLower
          ? { ...detail, counter: Math.max(0, detail.counter + amount) }
          : detail
      );
    } else {
      // Create new scope detail, ensure counter is not negative
      return [
        ...currentDetails,
        { scopeLabel: scopeName, counter: Math.max(0, amount) },
      ];
    }
  }

  /**
   * Helper to update learning counter for a measurement node interaction
   * - Validates that the measurement exists in takeoffData
   * - Optionally validates metadata.source is "backend" or "edited_backend" (if isAiOnly is true)
   * - Returns updated counter state with pageId or null if validation fails
   *
   * @param takeoffData - The unified measurement data
   * @param measurementId - ID of the measurement node
   * @param pageId - Page ID for the current page
   * @param learningCounter - All learning counter states indexed by page ID
   * @param isAiOnly - If true, only count AI-generated measurements (source: "backend" or "edited_backend")
   * @param amount - Amount to change counter by (default: 1 for increment, can be negative for decrement)
   * @returns Object with pageId and updated state, or null if validation fails
   */
  static changeLearningCounterForMeasurement(
    takeoffData: UnifiedMeasurementData | null,
    measurementId: string,
    pageId: number,
    learningCounter: Record<number, LearningCounterState>,
    isAiOnly: boolean = false,
    amount: number = 1
  ): { pageId: number; state: LearningCounterState } | null {
    if (!takeoffData?.result) return null;

    // Find the measurement in takeoffData
    const measurement = takeoffData.result
      .flatMap(
        (result) =>
          result.json.categories?.flatMap((category) =>
            category.groups.flatMap((group) => group.items)
          ) || []
      )
      .find((item) => item.id === measurementId);

    if (!measurement) return null;

    // If isAiOnly is true, check if source is backend or edited_backend
    if (isAiOnly) {
      if (
        measurement.metadata?.source !== "backend" &&
        measurement.metadata?.source !== "edited_backend"
      ) {
        return null;
      }
    }

    // Find the category name for this measurement
    const categoryWithMeasurement = takeoffData.result
      .flatMap((result) => result.json.categories || [])
      .find((category) =>
        category.groups.some((group) =>
          group.items.some((item) => item.id === measurementId)
        )
      );

    const scopeLabel = categoryWithMeasurement?.name || "Unknown";
    const currentState = learningCounter[pageId];
    const currentCounter = currentState?.counter ?? 0;
    const currentDetails = currentState?.details || [];

    // Update scope detail
    const updatedDetails = this.changeLearningScopeDetail(
      currentDetails,
      scopeLabel,
      amount
    );

    return {
      pageId,
      state: {
        counter: Math.max(0, currentCounter + amount),
        shouldAnimate: true,
        details: updatedDetails,
      },
    };
  }
}
